import React, { useEffect, useState } from "react";
import { database } from "../firebase";
import { ref, onValue, update } from "firebase/database";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { 
  ChevronLeft, 
  Power, 
  Timer, 
  MoreVertical,
  Clock,
  Calendar,
  RefreshCw
} from "lucide-react";

const DeviceMonitor = () => {
  const { espId } = useParams();
  const [relays, setRelays] = useState({});
  const [lastSeen, setLastSeen] = useState(null);
  const [activeTimerRef, setActiveTimerRef] = useState(null);
  const [minutes, setMinutes] = useState("");
  // Added a state to force re-render for "time ago" every minute
  const [, setTick] = useState(0);
  
  const navigate = useNavigate();
  const location = useLocation();
  const relayNames = location.state?.relayNames || [];


  useEffect(() => {
  const deviceRef = ref(database, espId);
  const unsubscribe = onValue(deviceRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.val();
      setRelays(data.relays || {});
      
      // FIXED ACCESS LOGIC:
      // Based on ESP32 code: data.deviceStatus.lastSeen
      if (data.deviceStatus && data.deviceStatus.lastSeen) {
        setLastSeen(data.deviceStatus.lastSeen);
      } else {
        setLastSeen(null);
      }
    } else {
      console.warn("Device snapshot does not exist");
    }
  });

  // Re-render relative time "2m ago" strings
  const interval = setInterval(() => setTick(t => t + 1), 10000); 

  return () => {
    unsubscribe();
    clearInterval(interval);
  };
}, [espId]);

  // useEffect(() => {
  //   const deviceRef = ref(database, espId);
  //   const unsubscribe = onValue(deviceRef, (snapshot) => {
  //     if (snapshot.exists()) {
  //       const data = snapshot.val();
  //       setRelays(data.relays || {});
  //       const statusObj = data.deviceStatus;
  //       const firstKey = Object.keys(statusObj || {})[0];
  //       if (firstKey) setLastSeen(statusObj[firstKey]?.lastSeen);
  //     }
  //   });

  //   // Update the "time ago" string every 30 seconds
  //   const interval = setInterval(() => setTick(t => t + 1), 30000);

  //   return () => {
  //     unsubscribe();
  //     clearInterval(interval);
  //   };
  // }, [espId]);

  // Helper: Calculate "How much time ago"
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return "Never";
    const now = Date.now();
    const diffInSeconds = Math.floor((now - timestamp) / 1000);

    if (diffInSeconds < 60) return "Just now";
    const mins = Math.floor(diffInSeconds / 60);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  // Helper: Format exact Date and Time
  const formatFullDate = (timestamp) => {
    if (!timestamp) return "No data";
    return new Date(timestamp).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const toggleRelay = async (key, currentState) => {
    const newState = currentState === "on" ? "off" : "on";
    await update(ref(database, `${espId}/relays`), { [key]: newState });
  };

  const handleSetTimer = async (key) => {
    const offAt = Date.now() + (parseInt(minutes) * 60000);
    await update(ref(database, `${espId}/relays`), { 
      [key]: "on",
      [`${key}_timer`]: offAt 
    });
    setActiveTimerRef(null);
    setMinutes("");
  };

  const isOnline = lastSeen ? (Date.now() - lastSeen) / 1000 / 60 < 2 : false;

  return (
    <div className="min-h-screen bg-[#F2F2F7] md:bg-white font-sans text-slate-900 pb-10">
      {/* --- Sleek Header --- */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-slate-100 rounded-full">
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-[15px] font-bold leading-tight">Terminal Control</h1>
            <div className="flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-green-500 shadow-[0_0_5px_#22c55e]' : 'bg-slate-300'}`} />
              <span className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">
                {isOnline ? 'Live Connection' : 'Offline'}
              </span>
            </div>
          </div>
        </div>
        <button className="p-2 hover:bg-slate-100 rounded-full">
          <MoreVertical className="w-5 h-5 text-slate-400" />
        </button>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-8">
        {/* --- Quick Stats Bar --- */}
        <div className="flex flex-wrap gap-2 mb-6">
          <div className="bg-white px-4 py-2 rounded-2xl border border-slate-200 flex items-center gap-2 shadow-sm">
            <Calendar className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-xs font-bold text-slate-700">
              {formatFullDate(lastSeen)}
            </span>
          </div>
          
          <div className="bg-white px-4 py-2 rounded-2xl border border-slate-200 flex items-center gap-2 shadow-sm">
            <RefreshCw className={`w-3.5 h-3.5 ${isOnline ? 'text-green-500' : 'text-slate-400'}`} />
            <span className="text-xs font-bold text-slate-600">
              {getRelativeTime(lastSeen)}
            </span>
          </div>

          <div className="bg-slate-100 px-4 py-2 rounded-2xl flex items-center gap-2 ml-auto">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">ID: {espId}</span>
          </div>
        </div>

        {/* --- COMPACT GRID --- */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {Object.entries(relays).map(([key, state], index) => {
            if (key.includes('_timer')) return null;
            const isOn = state === "on";
            const displayName = relayNames[index] || `R${index + 1}`;

            return (
              <div 
                key={key}
                className={`relative rounded-[28px] p-5 transition-all duration-500 cursor-pointer select-none group border h-40 flex flex-col justify-between
                  ${isOn 
                    ? 'bg-blue-600 border-blue-600 shadow-xl shadow-blue-100 text-white' 
                    : 'bg-white border-slate-200 hover:border-blue-300 text-slate-900 shadow-sm'}`}
                onClick={() => toggleRelay(key, state)}
              >
                <div className="flex justify-between items-start">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-colors
                    ${isOn ? 'bg-white/20' : 'bg-slate-100 text-slate-400'}`}>
                    <Power className="w-5 h-5" />
                  </div>
                  
                  <button 
                    onClick={(e) => { e.stopPropagation(); setActiveTimerRef(key); }}
                    className={`p-2 rounded-full transition-colors ${isOn ? 'hover:bg-blue-500 text-blue-100' : 'hover:bg-slate-100 text-slate-300'}`}
                  >
                    <Timer className="w-4 h-4" />
                  </button>
                </div>

                <div>
                  <h3 className="text-[15px] font-bold leading-tight truncate mb-1">
                    {displayName}
                  </h3>
                  <div className="flex items-center gap-1.5">
                    <div className={`w-1.5 h-1.5 rounded-full ${isOn ? 'bg-blue-200' : 'bg-slate-300'}`} />
                    <p className={`text-[10px] font-black uppercase tracking-widest ${isOn ? 'text-blue-100' : 'text-slate-400'}`}>
                      {isOn ? 'Active' : 'Standby'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* --- Timer Modal --- */}
      {activeTimerRef && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-sm rounded-[32px] p-6 shadow-2xl animate-in slide-in-from-bottom duration-300">
            <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-6 sm:hidden" />
            <h3 className="text-xl font-black mb-1">Set Auto-Off</h3>
            <p className="text-sm text-slate-500 mb-6">Device will trigger and auto-off after duration.</p>
            
            <div className="grid grid-cols-4 gap-2 mb-6">
              {[5, 15, 30, 60].map((val) => (
                <button 
                  key={val}
                  onClick={() => setMinutes(val.toString())}
                  className={`py-3 text-xs font-bold rounded-2xl border transition-all
                    ${minutes === val.toString() ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-100 text-slate-600'}`}
                >
                  {val}m
                </button>
              ))}
            </div>

            <input 
              type="number" 
              placeholder="Custom mins..."
              className="w-full bg-slate-100 border-none rounded-2xl px-5 py-4 mb-6 outline-none focus:ring-2 focus:ring-blue-600 transition-all font-bold text-lg"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />

            <div className="flex gap-3">
              <button 
                onClick={() => setActiveTimerRef(null)}
                className="flex-1 py-4 text-sm font-bold text-slate-400"
              >
                Cancel
              </button>
              <button 
                onClick={() => handleSetTimer(activeTimerRef)}
                className="flex-[2] py-4 bg-slate-900 text-white rounded-2xl font-bold shadow-lg"
              >
                Start Timer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceMonitor;